/**
 * Listings repository — the ONLY place touching the listings PUBLIC + STAFF
 * surface tables: `listings`, `property_requests`. Plus the two public RPCs
 * `increment_listing_view` (atomic counter) and `get_rating_request_by_token`
 * (token-scoped public read).
 *
 * (Off-plan projects — `projects`/`project_units`/`project_inquiries` — were
 * carved out into `src/repositories/projects.ts`; they are a separate domain.)
 *
 * Strangler-Fig per-slice migration (manifest-6, Track A8):
 *   Step 1 (done) — consolidated every scattered `supabase.from(<those 4 tables>)`
 *                   call + both RPCs here. Query shapes are BYTE-IDENTICAL to the
 *                   inline originals.
 *   Step 2 (done) — TWO endpoint classes in `apps/api/src/listings/*`:
 *                   • ListingsPublicController — UNAUTHENTICATED (no guard), mirrors
 *                     `apps/api/src/public-views/*`. Serves the anonymous marketplace
 *                     + SEO PropertyPublicPage.
 *                   • ListingsController — guarded `@RequirePermission('advertisements.*')`
 *                     for the staff /ads management surface.
 *   Step 3 (HERE) — the public reads flip to the public Nest endpoints (no auth);
 *                   the staff /ads ops flip to the guarded endpoints. Each method
 *                   tries Nest first (only when an API is configured) and falls back
 *                   to the original Supabase query — a reversible unit; local dev
 *                   without the Nest API keeps working. RLS is UNTOUCHED.
 *
 * ── Public-vs-staff authz split (the flip gotcha) ────────────────────────────
 *   PUBLIC (anonymous; flipped to UNGUARDED Nest, Supabase fallback):
 *     listPublic, getPublicById, listPublicCities, getPublicProperty,
 *     listPublicPropertyListings, submitPropertyRequest,
 *     incrementView (RPC), getRatingRequestByToken (RPC).
 *     These NEVER inject a company filter — the public marketplace surfaces every
 *     company's active listings (gated by the `is_active = true` RLS policy), and
 *     the rating-request token IS the access credential.
 *   STAFF (advertisements.*; flipped to GUARDED Nest, Supabase fallback):
 *     listForCompany, listByUnit, getByIdForCompany, create, update,
 *     listForProperty, listActiveByUnitIds, listStats, listPropertyRequestsStats.
 *     Every staff method scopes by `company_id` (the staff /ads screen gates on
 *     `advertisements.view`; the guarded endpoints enforce `advertisements.*`).
 *   KEPT on Supabase (cross-domain — NOT a listings table, documented):
 *     listAvailableUnitsForAd reads `units` (vacant-units picker); it lives here
 *     only because it is co-located with the create-ad flow. It is NOT flipped.
 */
import {
  isDefinitiveNotFound,
  isDomainRejection,
  reportNestFallback,
  reportNestFallbackOrRethrowUnknownCommit,
} from "@/lib/nestFallback";
import { z } from "zod";
import { listingSchema, shortIdFromParam } from "@dallali/shared";
import { isValidUUID } from "@/lib/uuid";
import { supabase, supabaseAnon } from "@/integrations/supabase/client";
import { serializeError } from "@/lib/sentry";
import type { Json } from "@/integrations/supabase/types";
import type { RegaDisplayData } from "@/types/rega";
import { API_ROUTES } from "@/api/routes";
import { API_URL } from "@/services/api";
import { makeNestRequest } from "@/services/nestRequest";


const ITEMS_PER_PAGE = 12;

/**
 * PostgREST `.or()` filter string for the read-time expiry guard (AC4, REGA
 * compliance fix 2026-07-05): a listing whose `expires_at` has passed must
 * disappear from public reads immediately, not just after the nightly Nest
 * `ListingsExpiryCronService` run. Keys ONLY off `expires_at` — NEVER the
 * REGA display date (`rega_display_data`/license end date), which is
 * display-only and can be stale — and NULL/future rows pass (Sarah's
 * constraint: an ad that only *displays* as expired but whose real
 * `expires_at` hasn't passed must stay visible).
 */
function notExpiredFilter(): string {
  return `expires_at.is.null,expires_at.gt.${new Date().toISOString()}`;
}

/**
 * Read-time REGA license guard (AC4, REGA compliance fix 2026-07-05,
 * companion to the Nest `ListingsLicenseExpiryCronService`): a REGA
 * electronic-monitoring letter flagged a public ad detail page
 * (`/advertisements/property/:listingId`) whose REGA ad license had expired —
 * this closes the window between a license lapsing and the next daily cron
 * run by re-checking license validity at read time too.
 *
 * Keys ONLY off the AUTHORITATIVE `rega_ad_licenses.license_end_date` /
 * `license_status` — NEVER `expires_at` and NEVER a merely displayed date.
 * Given a batch of rows (each optionally carrying `ad_license_number`),
 * returns only the ones whose license is currently valid (or that have no
 * REGA license at all — never touch a non-REGA ad).
 *
 * CONFIRMED LIMITATION (review finding on PR #3033, verified against the
 * local Supabase RLS policies): `rega_ad_licenses` has NO `anon`-readable
 * policy — its only SELECT policy (`rega_licenses_select_admin`) is scoped to
 * `authenticated` admins/super_admins of the owning company (it carries
 * advertiser PII: name, national ID, etc., so this is correct and should NOT
 * be widened just for this check). A public/anon read here therefore always
 * returns `{ data: [], error: null }` — `invalid` stays empty and every
 * listing silently passes.
 *
 * This is acceptable because this Supabase path is NOT the primary
 * enforcement point: `listingsRepository.getPublicById`/`getPublicCompanyIdOrThrow`
 * try the Nest endpoint FIRST, and a genuine Nest 404 (expired/cancelled
 * license, inactive, expired `expires_at`) is now surfaced directly rather
 * than falling through to this Supabase path (see the 404-handling fix in
 * `getPublicById` below). This function only executes when Nest is
 * unavailable/misconfigured (`VITE_API_URL` unset, or a genuine Nest
 * network/5xx/auth failure) — a degraded-mode fallback, not the compliance
 * backstop. The daily cron (`ListingsLicenseExpiryCronService`) remains the
 * authoritative, RLS-independent enforcement mechanism in all cases.
 */
async function filterOutExpiredRegaLicenses<T extends { ad_license_number?: string | null }>(
  rows: T[],
): Promise<T[]> {
  const licenseNumbers = rows
    .map((r) => r.ad_license_number)
    .filter((n): n is string => !!n);
  if (licenseNumbers.length === 0) return rows;

  const { data, error } = await supabase
    .from("rega_ad_licenses")
    .select("ad_license_number, license_end_date, license_status")
    .in("ad_license_number", licenseNumbers);
  if (error) throw error;

  const now = new Date();
  const invalid = new Set(
    (data ?? [])
      .filter(
        (l) =>
          (l.license_end_date && new Date(l.license_end_date) < now) ||
          ["expired", "cancelled", "canceled", "suspended"].includes(
            (l.license_status ?? "").toLowerCase(),
          ),
      )
      .map((l) => l.ad_license_number),
  );

  return rows.filter((r) => !r.ad_license_number || !invalid.has(r.ad_license_number));
}

// ─── Nest request helpers ─────────────────────────────────────────────────────

// UNAUTHENTICATED Nest request bound to the "listings public" surface — public
// marketplace/SEO endpoints need no Supabase session (only the gateway X-API-Key).
// Uses the shared transport (timeout + retry) with auth disabled.
const nestPublicRequest = makeNestRequest("listings public", { auth: false });

// Authenticated Nest request bound to the "listings" slice — shared transport
// (timeout + retry + typed NestRequestError). See src/services/nestRequest.ts.
const nestRequest = makeNestRequest("listings");

// ─── Public data shapes (verbatim from the hooks) ─────────────────────────────

export interface PublicListingProperty {
  id?: string;
  name: string;
  name_en?: string;
  /** Property purpose (residential/commercial/…) — used to match owner interests. */
  purpose?: string;
  city?: string;
  district?: string;
  address?: string;
  latitude?: number;
  longitude?: number;
  amenities?: {
    information?: { region?: string; streetName?: string; shortAddress?: string };
  } | null;
}

export interface PublicListing {
  id: string;
  title: string;
  title_en?: string;
  description?: string;
  listing_type: "rent" | "sale";
  price?: number;
  is_active: boolean;
  is_featured: boolean;
  views_count: number;
  created_at: string;
  image_urls?: string[];
  rega_display_data?: RegaDisplayData | null;
  ad_license_number?: string | null;
  property: PublicListingProperty | null;
  unit: {
    id: string;
    unit_number: string;
    unit_type: string;
    bedrooms?: number;
    bathrooms?: number;
    area?: number;
    images?: string[];
    amenities?: string[];
  } | null;
}

export interface ListingFilters {
  purpose?: string;
  city?: string;
  category?: string;
  search?: string;
  minPrice?: number;
  maxPrice?: number;
  bedrooms?: number;
  bathrooms?: number;
  minArea?: number;
  maxArea?: number;
}

export interface PublicListingsPage {
  listings: PublicListing[];
  total: number;
  totalPages: number;
}

export interface SubmitPropertyRequestData {
  listing_id: string;
  company_id: string;
  visitor_name: string;
  visitor_phone: string;
  visitor_email?: string;
  message?: string;
  request_type: "visit" | "info" | "contract";
}

export interface CreateListingData {
  company_id: string;
  unit_id: string;
  property_id: string;
  title: string;
  description?: string;
  listing_type: "rent" | "sale";
  price?: number;
  published_at?: string;
  expires_at?: string;
  rega_display_data?: RegaDisplayData | null;
  ad_license_number?: string | null;
  image_urls: string[];
  is_active: boolean;
}

export interface UpdateListingData {
  title?: string;
  description?: string;
  listing_type?: "rent" | "sale";
  price?: number;
  is_active?: boolean;
  published_at?: string;
  expires_at?: string;
  image_urls?: string[];
}

// ─── Supabase implementations: PUBLIC reads ──────────────────────────────────

async function listPublicViaSupabase(
  filters: ListingFilters,
  page: number,
): Promise<PublicListingsPage> {
  let query = supabase
    .from("listings")
    .select(
      `
      *,
      property:properties(name, name_en, city, district, address, latitude, longitude, amenities, purpose, units_count),
      unit:units(id, unit_number, unit_type, bedrooms, bathrooms, area, images, amenities)
    `,
      { count: "exact" },
    )
    .eq("is_active", true)
    // Public reads never show an ad without a REGA ad licence (mirrors the Nest publicWhere).
    .not("ad_license_number", "is", null)
    .neq("ad_license_number", "")
    .or(notExpiredFilter())
    .order("is_featured", { ascending: false })
    .order("created_at", { ascending: false });

  if (filters.purpose && filters.purpose !== "all") {
    query = query.eq("listing_type", filters.purpose);
  }

  const from = (page - 1) * ITEMS_PER_PAGE;
  const to = from + ITEMS_PER_PAGE - 1;
  query = query.range(from, to);

  const { data, error, count } = await query;
  if (error) throw error;

  // AC4 — never serve a row whose REGA license has since expired/been
  // cancelled, even if the cron hasn't caught it yet. `count`/pagination are
  // not corrected for this rare edge (self-heals within a day via the cron).
  const licenseValid = await filterOutExpiredRegaLicenses(data ?? []);

  const filtered = applyClientSideListingFilters(
    licenseValid.map((item) => ({
      ...item,
      rega_display_data: item.rega_display_data as RegaDisplayData | null,
    })),
    filters,
  );

  return {
    listings: filtered as unknown as PublicListing[],
    total: count || 0,
    totalPages: Math.ceil((count || 0) / ITEMS_PER_PAGE),
  };
}

/**
 * Client-side filters (city/category/search/beds/baths/price) applied verbatim
 * from `usePublicListings`. Shared by the Supabase path and the Nest-flip path
 * so server-side pagination + client-side narrowing stay byte-identical.
 */
function applyClientSideListingFilters(
  rows: Array<Record<string, unknown>>,
  filters: ListingFilters,
): Array<Record<string, unknown>> {
  let filtered = rows;

  if (filters.city) {
    filtered = filtered.filter((l) =>
      (l.property as { city?: string } | null)?.city
        ?.toLowerCase()
        .includes(filters.city!.toLowerCase()),
    );
  }

  if (filters.category && filters.category !== "all") {
    filtered = filtered.filter(
      (l) => (l.unit as { unit_type?: string } | null)?.unit_type === filters.category,
    );
  }

  if (filters.search) {
    const s = filters.search.toLowerCase();
    filtered = filtered.filter((l) => {
      const property = l.property as { city?: string; district?: string } | null;
      return (
        (l.title as string | undefined)?.toLowerCase().includes(s) ||
        (l.title_en as string | undefined)?.toLowerCase().includes(s) ||
        property?.city?.toLowerCase().includes(s) ||
        property?.district?.toLowerCase().includes(s)
      );
    });
  }

  if (filters.bedrooms && filters.bedrooms > 0) {
    filtered = filtered.filter(
      (l) => ((l.unit as { bedrooms?: number } | null)?.bedrooms ?? -Infinity) >= filters.bedrooms!,
    );
  }

  if (filters.bathrooms && filters.bathrooms > 0) {
    filtered = filtered.filter(
      (l) => ((l.unit as { bathrooms?: number } | null)?.bathrooms ?? -Infinity) >= filters.bathrooms!,
    );
  }

  if (filters.minPrice) {
    filtered = filtered.filter((l) => ((l.price as number | undefined) ?? -Infinity) >= filters.minPrice!);
  }

  if (filters.maxPrice) {
    filtered = filtered.filter((l) => ((l.price as number | undefined) ?? Infinity) <= filters.maxPrice!);
  }

  return filtered;
}

/** `idOrSlug`: a full UUID or a slug URL segment ending in the 8-hex short id (P1-2). */
async function getPublicByIdViaSupabase(idOrSlug: string) {
  const shortId = shortIdFromParam(idOrSlug);
  if (!shortId) return null;
  const base = supabase
    .from("listings")
    .select(
      `
      *,
      property:properties(id, name, name_en, city, district, address, latitude, longitude, amenities, units_count),
      unit:units(id, unit_number, unit_type, bedrooms, bathrooms, area, images, amenities, living_rooms, floor_number, rent_amount, is_furnished)
    `,
    );
  // A slug carries only the start of the UUID; UUIDs sort bytewise, so it's a
  // range (oldest row wins on a shared prefix, as in the Nest repository).
  const { data, error } = await (isValidUUID(idOrSlug)
    ? base.eq("id", idOrSlug)
    : base
        .gte("id", `${shortId}-0000-0000-0000-000000000000`)
        .lte("id", `${shortId}-ffff-ffff-ffff-ffffffffffff`)
        .order("created_at", { ascending: true })
        .limit(1)
  )
    .eq("is_active", true)
    // Public reads never show an ad without a REGA ad licence (mirrors the Nest publicWhere).
    .not("ad_license_number", "is", null)
    .neq("ad_license_number", "")
    .or(notExpiredFilter())
    .maybeSingle();

  if (error) throw error;
  if (!data) return data;

  // AC4 — this is the exact surface REGA's 2026-07-05 letter cited (a public
  // ad detail URL): re-check REGA license validity so an ad whose license has
  // expired/been cancelled since the last cron run is treated as not found,
  // same as an inactive one.
  const [licenseValid] = await filterOutExpiredRegaLicenses([data]);
  return licenseValid ?? null;
}

async function listPublicCitiesViaSupabase(): Promise<string[]> {
  const { data, error } = await supabase
    .from("listings")
    .select(`property:properties(city)`)
    .eq("is_active", true)
    // Public reads never show an ad without a REGA ad licence (mirrors the Nest publicWhere).
    .not("ad_license_number", "is", null)
    .neq("ad_license_number", "")
    .or(notExpiredFilter());

  if (error) throw error;

  return [
    ...new Set(
      data
        ?.map((l) => (l.property as { city?: string } | null)?.city)
        .filter(Boolean),
    ),
  ] as string[];
}

// ─── Supabase implementations: PUBLIC writes ─────────────────────────────────

async function submitPropertyRequestViaSupabase(data: SubmitPropertyRequestData) {
  const { data: result, error } = await supabase
    .from("property_requests")
    .insert({
      listing_id: data.listing_id,
      company_id: data.company_id,
      visitor_name: data.visitor_name,
      visitor_phone: data.visitor_phone,
      visitor_email: data.visitor_email,
      message: data.message,
      request_type: data.request_type,
      status: "pending",
    })
    .select()
    .single();

  if (error) throw error;
  return result;
}

// ─── Supabase implementations: PUBLIC property page (anon client) ────────────

/**
 * The SEO PropertyPublicPage uses the ANON Supabase client to bypass the
 * authenticated RLS policies — the page must render whether or not a user is
 * logged in. Preserved verbatim (supabaseAnon, not supabase).
 */
/**
 * Public property row shape. `public_units_source` is not yet in the generated
 * Supabase types (added by the property public page settings migration), so the
 * anon read is cast until `types.ts` is regenerated — same pattern the codebase
 * uses for other not-yet-typed columns/tables.
 */
type PublicPropertyRow = {
  id: string;
  name: string;
  name_en: string | null;
  description: string | null;
  description_en: string | null;
  property_type: string;
  purpose: string | null;
  address: string | null;
  city: string | null;
  district: string | null;
  postal_code: string | null;
  latitude: number | null;
  longitude: number | null;
  total_area: number | null;
  floors_count: number | null;
  built_year: number | null;
  units_count: number | null;
  images: unknown;
  amenities: unknown;
  public_units_source: string;
  company_id: string;
};

/** `idOrSlug`: a full UUID or a slug URL segment ending in the 8-hex short id (P1-2). */
async function getPublicPropertyViaSupabase(idOrSlug: string): Promise<PublicPropertyRow | null> {
  const shortId = shortIdFromParam(idOrSlug);
  if (!shortId) return null;
  // `public_units_source` is not yet in the generated Supabase types, so the
  // typed `.select()` rejects the column. Cast the query builder until types.ts
  // is regenerated (same approach the codebase uses for other untyped columns).
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const base = (supabaseAnon.from("properties") as any).select(
    "id, name, name_en, description, description_en, property_type, purpose, address, city, district, postal_code, latitude, longitude, total_area, floors_count, built_year, units_count, images, amenities, public_units_source, company_id",
  );
  const { data, error } = await (isValidUUID(idOrSlug)
    ? base.eq("id", idOrSlug)
    : base
        .gte("id", `${shortId}-0000-0000-0000-000000000000`)
        .lte("id", `${shortId}-ffff-ffff-ffff-ffffffffffff`)
        .order("created_at", { ascending: true })
        .limit(1)
  )
    .eq("is_active", true)
    .maybeSingle();

  if (error) throw error;
  return (data as PublicPropertyRow | null) ?? null;
}

/**
 * Anon read of a property's VACANT (available) units, for the public property
 * page when the property opts into `public_units_source = 'vacant_units'`. Gated
 * by the "Public can view vacant units of opted-in properties" RLS policy — anon
 * only sees available units of an active, opted-in property. `units` is not a
 * flipped domain (manifest-6), so this stays a Supabase anon read with no Nest
 * counterpart.
 */
async function listPublicPropertyVacantUnitsViaSupabase(propertyId: string) {
  const { data, error } = await supabaseAnon
    .from("units")
    .select(
      "id, unit_number, unit_type, area, bedrooms, bathrooms, rent_amount, annual_rent, sale_price, images",
    )
    .eq("property_id", propertyId)
    .eq("status", "available")
    .order("unit_number", { ascending: true });

  if (error) throw error;
  return data ?? [];
}

async function listPublicPropertyListingsViaSupabase(propertyId: string) {
  const { data, error } = await supabaseAnon
    .from("listings")
    .select(
      `
      id, title, title_en, description, listing_type, price, is_negotiable, image_urls, ad_license_number, rega_display_data,
      unit:units(unit_number, unit_type, area, bedrooms, bathrooms)
    `,
    )
    .eq("property_id", propertyId)
    .eq("is_active", true)
    // Public reads never show an ad without a REGA ad licence (mirrors the Nest publicWhere).
    .not("ad_license_number", "is", null)
    .neq("ad_license_number", "")
    .or(notExpiredFilter())
    .order("created_at", { ascending: false });

  if (error) throw error;

  // AC4 — same REGA license read-time guard as the other public reads.
  return filterOutExpiredRegaLicenses(data ?? []);
}

// ─── Supabase implementations: STAFF (company-scoped) ────────────────────────

async function listForCompanyViaSupabase(companyId: string) {
  const { data, error } = await supabase
    .from("listings")
    .select(
      `
      *,
      property:properties(name, name_en, city, district, purpose, latitude, longitude),
      unit:units(unit_number, unit_type, area)
    `,
    )
    .eq("company_id", companyId)
    .order("created_at", { ascending: false });

  if (error) throw error;
  return data;
}

async function listByUnitViaSupabase(unitId: string, companyId: string) {
  const { data, error } = await supabase
    .from("listings")
    .select(
      `
      *,
      property:properties(id, name, name_en, city, district, purpose),
      unit:units(id, unit_number, unit_type, area, bedrooms, bathrooms)
    `,
    )
    .eq("company_id", companyId)
    .eq("unit_id", unitId)
    .order("created_at", { ascending: false });

  if (error) throw error;
  return data;
}

async function getByIdForCompanyViaSupabase(id: string, companyId: string) {
  const { data, error } = await supabase
    .from("listings")
    .select(
      `
      *,
      property:properties(id, name, name_en, city, district, address),
      unit:units(id, unit_number, unit_type, area, bedrooms, bathrooms, rent_amount, images, status)
    `,
    )
    .eq("id", id)
    .eq("company_id", companyId)
    .maybeSingle();

  if (error) throw error;
  if (!data) return null;
  return { ...data, rega_display_data: data.rega_display_data as RegaDisplayData | null };
}

async function createViaSupabase(data: CreateListingData) {
  const { data: result, error } = await supabase
    .from("listings")
    .insert({
      ...data,
      rega_display_data: (data.rega_display_data ?? null) as Json,
    })
    .select()
    .single();

  if (error) throw error;
  return result;
}

async function updateViaSupabase(id: string, companyId: string, updates: UpdateListingData) {
  const { data: result, error } = await supabase
    .from("listings")
    .update(updates)
    .eq("id", id)
    .eq("company_id", companyId)
    .select()
    .maybeSingle();

  if (error) throw error;
  if (!result) throw new Error("Listing not found or access denied");
  return result;
}

async function listForPropertyViaSupabase(propertyId: string, companyId: string) {
  const { data, error } = await supabase
    .from("listings")
    .select(
      `
      *,
      unit:units(id, unit_number, unit_type, area, bedrooms, bathrooms, annual_rent)
    `,
    )
    .eq("company_id", companyId)
    .eq("property_id", propertyId)
    .order("created_at", { ascending: false });

  if (error) throw error;
  return data;
}

async function listActiveUnitIdsViaSupabase(companyId: string): Promise<string[]> {
  const { data, error } = await supabase
    .from("listings")
    .select("unit_id")
    .eq("company_id", companyId)
    .eq("is_active", true);

  if (error) throw error;
  return (data ?? []).map((l) => l.unit_id);
}

async function listActiveByUnitIdsViaSupabase(unitIds: string[], companyId: string) {
  const { data, error } = await supabase
    .from("listings")
    .select("id, unit_id, title, is_active")
    .eq("company_id", companyId)
    .in("unit_id", unitIds)
    .eq("is_active", true);

  if (error) throw error;
  return data ?? [];
}

export interface ListingStatsRow {
  id: string;
  is_active: boolean | null;
  expires_at: string | null;
}

async function listStatsViaSupabase(
  companyId: string,
  range?: { startDate?: string; endDate?: string },
): Promise<ListingStatsRow[]> {
  let query = supabase
    .from("listings")
    .select("id, is_active, expires_at")
    .eq("company_id", companyId);
  if (range?.startDate) query = query.gte("created_at", range.startDate);
  if (range?.endDate) query = query.lte("created_at", range.endDate);

  const { data, error } = await query;

  if (error) throw error;
  return (data ?? []) as ListingStatsRow[];
}

export interface PropertyRequestStatsRow {
  id: string;
  status: string | null;
  request_type: string | null;
}

async function listPropertyRequestsStatsViaSupabase(
  companyId: string,
): Promise<PropertyRequestStatsRow[]> {
  const { data, error } = await supabase
    .from("property_requests")
    .select("id, status, request_type")
    .eq("company_id", companyId)
    .neq("request_type", "visit");

  if (error) throw error;
  return (data ?? []) as PropertyRequestStatsRow[];
}

// ─── Repository ───────────────────────────────────────────────────────────────

export const listingsRepository = {
  // ── PUBLIC reads (flipped to UNGUARDED Nest, Supabase fallback) ────────────

  /** Public marketplace listings page (server-paginated + client-narrowed). */
  async listPublic(filters: ListingFilters, page = 1): Promise<PublicListingsPage> {
    if (API_URL) {
      try {
        const rows = await nestPublicRequest<{
          listings: unknown[];
          total: number;
        }>(
          `${API_ROUTES.listings.publicList()}?purpose=${encodeURIComponent(
            filters.purpose ?? "all",
          )}&page=${page}`,
        );
        const parsed = z.array(listingSchema).parse(rows.listings) as unknown as Array<
          Record<string, unknown>
        >;
        const filtered = applyClientSideListingFilters(parsed, filters);
        return {
          listings: filtered as unknown as PublicListing[],
          total: rows.total,
          totalPages: Math.ceil(rows.total / ITEMS_PER_PAGE),
        };
      } catch (err) {
        reportNestFallback("listings.listPublic", err);
      }
    }
    return listPublicViaSupabase(filters, page);
  },

  /** Single public listing by id (active only). */
  async getPublicById(id: string): Promise<Awaited<ReturnType<typeof getPublicByIdViaSupabase>>> {
    if (API_URL) {
      try {
        const row = await nestPublicRequest<unknown>(API_ROUTES.listings.publicGetById(id));
        return (row ? listingSchema.parse(row) : null) as Awaited<
          ReturnType<typeof getPublicByIdViaSupabase>
        >;
      } catch (err) {
        // A deterministic 404 is Nest's REAL answer -- genuinely missing,
        // inactive, expired, OR REGA-license-invalid (AC4, 2026-07-05). Falling
        // back to Supabase would re-serve a listing Nest correctly hid: the
        // Supabase fallback CANNOT replicate the REGA-license check
        // (rega_ad_licenses has no anon RLS policy -- it carries advertiser
        // PII -- so filterOutExpiredRegaLicenses silently sees "no invalid
        // licenses" there and the ad would leak back through). Surface "not
        // found" directly; keep falling back for network/5xx/auth failures
        // (mirrors owners.create's precedent for deterministic-4xx handling).
        if (isDefinitiveNotFound(err)) {
          return null as Awaited<ReturnType<typeof getPublicByIdViaSupabase>>;
        }
        reportNestFallback("listings.getPublicById", err);
      }
    }
    return getPublicByIdViaSupabase(id);
  },

  /** Distinct cities across active public listings (filter dropdown). */
  async listPublicCities(): Promise<string[]> {
    if (API_URL) {
      try {
        return await nestPublicRequest<string[]>(API_ROUTES.listings.publicCities());
      } catch (err) {
        reportNestFallback("listings.listPublicCities", err);
      }
    }
    return listPublicCitiesViaSupabase();
  },

  // ── PUBLIC writes (anonymous visitor leads) ────────────────────────────────

  /**
   * Public visitor's property request (visit/info/contract). No company filter —
   * the company_id is supplied by the public page (the listing's owning company).
   */
  async submitPropertyRequest(data: SubmitPropertyRequestData) {
    if (API_URL) {
      try {
        return await nestPublicRequest<unknown>(
          API_ROUTES.listings.submitPropertyRequest(),
          { method: "POST", body: JSON.stringify(data) },
        );
      } catch (err) {
        reportNestFallback("listings.submitPropertyRequest", err);
      }
    }
    return submitPropertyRequestViaSupabase(data);
  },

  // ── PUBLIC property page (anonymous SEO) ───────────────────────────────────

  async getPublicProperty(
    id: string,
  ): Promise<Awaited<ReturnType<typeof getPublicPropertyViaSupabase>>> {
    if (API_URL) {
      try {
        return (await nestPublicRequest<unknown>(
          API_ROUTES.listings.publicProperty(id),
        )) as Awaited<ReturnType<typeof getPublicPropertyViaSupabase>>;
      } catch (err) {
        // The Nest 404 (lst-003) is the ANSWER — the property doesn't exist or
        // is inactive, and the Supabase path would find the same null. Falling
        // back only re-runs the miss and files nest-fallback noise for every
        // dead link (DALLALI-FRONTEND-HB / #3675). Same #3397-class guard as
        // getPublicById above.
        if (isDefinitiveNotFound(err)) {
          return null as Awaited<ReturnType<typeof getPublicPropertyViaSupabase>>;
        }
        reportNestFallback("listings.getPublicProperty", err);
      }
    }
    return getPublicPropertyViaSupabase(id);
  },

  async listPublicPropertyListings(
    propertyId: string,
  ): Promise<Awaited<ReturnType<typeof listPublicPropertyListingsViaSupabase>>> {
    if (API_URL) {
      try {
        const rows = await nestPublicRequest<unknown[]>(
          API_ROUTES.listings.publicByProperty(propertyId),
        );
        return z.array(listingSchema).parse(rows) as Awaited<
          ReturnType<typeof listPublicPropertyListingsViaSupabase>
        >;
      } catch (err) {
        reportNestFallback("listings.listPublicPropertyListings", err);
      }
    }
    return listPublicPropertyListingsViaSupabase(propertyId);
  },

  /**
   * Vacant (available) units of a property for the public page. Supabase-anon
   * only — `units` is not a flipped domain, so there is no Nest counterpart.
   */
  async listPublicPropertyVacantUnits(
    propertyId: string,
  ): Promise<Awaited<ReturnType<typeof listPublicPropertyVacantUnitsViaSupabase>>> {
    return listPublicPropertyVacantUnitsViaSupabase(propertyId);
  },

  // ── PUBLIC RPCs (atomic) ───────────────────────────────────────────────────

  /**
   * Atomic per-listing view bump. NEVER read-modify-write in app code (race):
   * the Postgres RPC `increment_listing_view` does `UPDATE … SET views = views + 1`
   * in one statement. The Nest public path forwards to the SAME RPC server-side.
   */
  async incrementView(listingId: string): Promise<void> {
    if (API_URL) {
      try {
        await nestPublicRequest<void>(API_ROUTES.listings.incrementView(listingId), {
          method: "POST",
        });
        return;
      } catch (err) {
        reportNestFallback("listings.incrementView", err);
      }
    }
    const { error } = await supabase.rpc("increment_listing_view", {
      p_listing_id: listingId,
    });
    if (error) throw error;
  },

  /**
   * Token-scoped public rating-request lookup. The token IS the access credential
   * (no company/auth context). Forwards to the SAME RPC `get_rating_request_by_token`.
   */
  async getRatingRequestByToken(token: string) {
    if (API_URL) {
      try {
        return await nestPublicRequest<unknown>(
          API_ROUTES.listings.ratingRequestByToken(token),
        );
      } catch (err) {
        reportNestFallback("listings.getRatingRequestByToken", err);
      }
    }
    const { data, error } = await supabase.rpc("get_rating_request_by_token", {
      p_token: token,
    });
    if (error) throw error;
    return data;
  },

  // ── STAFF reads (advertisements.*; flipped to GUARDED Nest, fallback) ──────

  /** Company listings list for the staff /ads screen, newest first. */
  async listForCompany(
    companyId: string,
  ): Promise<Awaited<ReturnType<typeof listForCompanyViaSupabase>>> {
    if (API_URL) {
      try {
        const rows = await nestRequest<unknown[]>(API_ROUTES.listings.list());
        return z.array(listingSchema).parse(rows) as Awaited<
          ReturnType<typeof listForCompanyViaSupabase>
        >;
      } catch (err) {
        reportNestFallback("listings.listForCompany", err);
      }
    }
    return listForCompanyViaSupabase(companyId);
  },

  /** Listings for one unit (company-scoped). */
  async listByUnit(
    unitId: string,
    companyId: string,
  ): Promise<Awaited<ReturnType<typeof listByUnitViaSupabase>>> {
    if (API_URL) {
      try {
        const rows = await nestRequest<unknown[]>(API_ROUTES.listings.byUnit(unitId));
        return z.array(listingSchema).parse(rows) as Awaited<
          ReturnType<typeof listByUnitViaSupabase>
        >;
      } catch (err) {
        // No isDefinitiveNotFound guard here (unlike the getById reads):
        // this hits the shared query-param route GET /listings?unit_id= and
        // the service never throws NotFoundException, so a 404 can only be
        // route-level (bad deploy) — keep it loud (PR #3397 review).
        reportNestFallback("listings.listByUnit", err);
      }
    }
    return listByUnitViaSupabase(unitId, companyId);
  },

  /** Single listing detail (company-scoped). Cross-tenant → Nest 404 → null. */
  async getByIdForCompany(
    id: string,
    companyId: string,
  ): Promise<Awaited<ReturnType<typeof getByIdForCompanyViaSupabase>>> {
    if (API_URL) {
      try {
        const row = await nestRequest<unknown>(API_ROUTES.listings.getById(id));
        return (row ? listingSchema.parse(row) : null) as Awaited<
          ReturnType<typeof getByIdForCompanyViaSupabase>
        >;
      } catch (err) {
        if (isDefinitiveNotFound(err)) return null; // app-level 404 = the real answer (#3397 class)
        reportNestFallback("listings.getByIdForCompany", err);
      }
    }
    return getByIdForCompanyViaSupabase(id, companyId);
  },

  /** Listings for a property (company-scoped) — property detail tab. */
  async listForProperty(
    propertyId: string,
    companyId: string,
  ): Promise<Awaited<ReturnType<typeof listForPropertyViaSupabase>>> {
    if (API_URL) {
      try {
        const rows = await nestRequest<unknown[]>(API_ROUTES.listings.byProperty(propertyId));
        return z.array(listingSchema).parse(rows) as Awaited<
          ReturnType<typeof listForPropertyViaSupabase>
        >;
      } catch (err) {
        reportNestFallback("listings.listForProperty", err);
      }
    }
    return listForPropertyViaSupabase(propertyId, companyId);
  },

  /** Active listings for a set of units (company-scoped) — units-with-contracts map. */
  async listActiveByUnitIds(unitIds: string[], companyId: string) {
    if (unitIds.length === 0) return [];
    return listActiveByUnitIdsViaSupabase(unitIds, companyId);
  },

  /**
   * unit_ids of the company's active listings — used by the "available units for
   * ad" picker to exclude already-advertised units. Kept on Supabase (the picker's
   * main query reads the cross-domain `units` table, which is NOT a listings slice
   * table and stays inline in the hook).
   */
  async listActiveUnitIds(companyId: string): Promise<string[]> {
    return listActiveUnitIdsViaSupabase(companyId);
  },

  /** Lightweight stats projection for the /ads dashboard counters. */
  async listStats(
    companyId: string,
    range?: { startDate?: string; endDate?: string },
  ): Promise<ListingStatsRow[]> {
    if (API_URL) {
      try {
        const rows = await nestRequest<unknown[]>(API_ROUTES.listings.stats(range?.startDate, range?.endDate));
        return rows as ListingStatsRow[];
      } catch (err) {
        reportNestFallback("listings.listStats", err);
      }
    }
    return listStatsViaSupabase(companyId, range);
  },

  /** Property-requests stats projection (excludes visits) for the dashboard. */
  async listPropertyRequestsStats(companyId: string): Promise<PropertyRequestStatsRow[]> {
    if (API_URL) {
      try {
        const rows = await nestRequest<unknown[]>(API_ROUTES.listings.propertyRequestsStats());
        return rows as PropertyRequestStatsRow[];
      } catch (err) {
        reportNestFallback("listings.listPropertyRequestsStats", err);
      }
    }
    return listPropertyRequestsStatsViaSupabase(companyId);
  },

  // ── STAFF writes ───────────────────────────────────────────────────────────

  /**
   * Create a listing. company_id is injected from the JWT server-side; the
   * Supabase fallback uses the caller-supplied company_id.
   *
   * NON-IDEMPOTENT write → duplicate-write guard (#3316 / #3399): the Supabase
   * fallback only runs when Nest verifiably did not commit (safe 4xx /
   * unconfigured). An ambiguous outcome (network, timeout, 5xx, parse failure
   * after a 2xx) RETHROWS instead of re-issuing the insert — a fallback after a
   * committed-but-lost Nest write would double-create the listing.
   */
  async create(
    data: CreateListingData,
  ): Promise<Awaited<ReturnType<typeof createViaSupabase>>> {
    if (API_URL) {
      try {
        const row = await nestRequest<unknown>(API_ROUTES.listings.create(), {
          method: "POST",
          // company_id is injected server-side from the JWT; the DTO accepts-but-ignores it.
          body: JSON.stringify(data),
        });
        return listingSchema.parse(row) as Awaited<ReturnType<typeof createViaSupabase>>;
      } catch (err) {
        // A 422 (e.g. publishing without an ad licence) is the server's answer, not an outage.
        if (isDomainRejection(err)) throw err;
        reportNestFallbackOrRethrowUnknownCommit("listings.create", err);
      }
    }
    return createViaSupabase(data);
  },

  /** Update a listing (company-scoped). */
  async update(
    id: string,
    companyId: string,
    updates: UpdateListingData,
  ): Promise<Awaited<ReturnType<typeof updateViaSupabase>>> {
    if (API_URL) {
      try {
        const row = await nestRequest<unknown>(API_ROUTES.listings.update(id), {
          method: "PUT",
          body: JSON.stringify(updates),
        });
        return listingSchema.parse(row) as Awaited<ReturnType<typeof updateViaSupabase>>;
      } catch (err) {
        // A 422 (e.g. activating without an ad licence) is the server's answer, not an outage.
        if (isDomainRejection(err)) throw err;
        reportNestFallback("listings.update", err);
      }
    }
    return updateViaSupabase(id, companyId, updates);
  },

  /**
   * Trigger AR↔EN auto-translation for a listing. Idempotent: re-running on
   * a fully-translated row is a no-op server-side. NEVER throws — failure is
   * swallowed and reported to the console so the surrounding create/update
   * mutation's success toast still fires. Falls back to a no-op when the
   * Nest API isn't configured (Supabase-only environments don't have the
   * translation worker, so the listing just stays in `translation_state =
   * 'pending'` until the next save or the backfill script runs).
   */
  async translate(id: string): Promise<void> {
    if (!API_URL) return;
    try {
      await nestRequest<unknown>(API_ROUTES.listings.translate(id), {
        method: "POST",
      });
    } catch (err) {
      // Soft-fail — caller (useCreateListing / useUpdateListing) has already
      // committed the row, so we just log and move on.
      reportNestFallback("listings.translate", err);
    }
  },
};
